// Builds a PROTOTYPE_CLIPS-compatible clip set for a UAL2 (Quaternius
// "Universal Animation Library 2") mesh: the canonical rig's own 45 baked
// clips, retargeted onto UAL2's skeleton via the same retargetClip/
// suggestBoneMapping primitives the custom-rig bone-mapping UI uses (see
// retarget.ts, importRig.ts). This is a *built-in* rig option (bundled
// asset + curated synonym table in retarget.ts), not the interactive
// upload-and-map flow SF22 built for a creator's own GLB - so this module
// runs the same algorithm once, automatically, with no manual mapping step.
//
// Pure and dependency-light (only 'three'), so it is directly unit-testable
// against real parsed GLBs (see tests/unit/ual2Rig.test.ts) without a
// browser: no fetch, no React, no DOM.
//
// Kept deliberately identical to its sibling copy at
// sunder/sstk-sunder-web/src/prototype/src/ual2Retarget.ts (see retarget.ts's
// own header for why the underlying algorithm is duplicated into Sunder).
import * as THREE from 'three';

import { findSkinnedMesh, walkSkeleton } from './importRig';
import { buildRestPose, retargetClip, suggestBoneMapping, type BoneMapping } from './retarget';

/** Minimal shape this module needs from a parsed glTF - a scene graph
 * containing exactly one SkinnedMesh. Matches PrototypeGltf's `scene` field
 * without importing the animation loader (which would pull in the browser
 * GLTFLoader path); callers pass either a live PrototypeGltf or a parsed
 * GLTF.scene directly. */
export interface RiggedScene {
  scene: THREE.Object3D;
}

export interface Ual2PrototypeGltf {
  /** UAL2's own scene graph, with every bone that resolved to a canonical
   * joint renamed to that joint's canonical name (see below) - the returned
   * `animations` tracks target these renamed bones directly. */
  scene: THREE.Object3D;
  /** The canonical rig's 45 clips, retargeted onto UAL2's rest pose. Clip
   * names (and so withClipPrefix/PROTOTYPE_CLIPS lookups) are unchanged. */
  animations: THREE.AnimationClip[];
  /** Canonical joint name -> UAL2's own (pre-rename) joint name, as
   * auto-suggested. Exposed for diagnostics/tests, not needed to play the
   * returned animations (the scene's bones are already renamed to match). */
  mapping: BoneMapping;
  /** Canonical joints the clip set animates that had no UAL2 counterpart -
   * their tracks are dropped from every retargeted clip. Empty for a fully
   * resolved skeleton. */
  unmappedBones: string[];
}

/**
 * Retarget the canonical rig's clips onto a UAL2 mesh's own skeleton.
 *
 * Unlike the interactive BoneMappingPanel flow (which keeps the uploaded
 * rig's own joint names and renames retargeted *tracks* to match), this
 * renames the UAL2 *scene's bones* themselves to their resolved canonical
 * name first, then retargets with an identity mapping over just the
 * resolved joints. The result: PROTOTYPE_SKELETON.handBones/headBone/
 * rootBone, createPrototypeInstance's attachToHand/getBone, and every other
 * canonical-name lookup work unmodified against the returned scene - this
 * built-in rig option needs no rig-aware branches anywhere downstream.
 * A UAL2 joint with no canonical counterpart (finger/toe tip "leaf" bones,
 * which the canonical rig does not model) keeps its own original name,
 * present but unanimated.
 */
export function buildUal2PrototypeGltf(canonical: { scene: THREE.Object3D; animations: THREE.AnimationClip[] }, ual2: RiggedScene): Ual2PrototypeGltf {
  const canonicalMesh = findSkinnedMesh(canonical.scene);
  if (!canonicalMesh) throw new Error('buildUal2PrototypeGltf: no SkinnedMesh found in the canonical rig.');
  const canonicalBones = walkSkeleton(canonicalMesh);
  const canonicalRest = buildRestPose(canonicalBones);
  const canonicalNames = canonicalBones.map((b) => b.name);

  const ual2Mesh = findSkinnedMesh(ual2.scene);
  if (!ual2Mesh) throw new Error('buildUal2PrototypeGltf: no SkinnedMesh found in the UAL2 rig.');
  const ual2Names = ual2Mesh.skeleton.bones.map((b) => b.name);
  const mapping = suggestBoneMapping(canonicalNames, ual2Names);

  const bonesBySourceName = new Map(ual2Mesh.skeleton.bones.map((b) => [b.name, b]));
  for (const [canonicalName, sourceName] of Object.entries(mapping)) {
    const bone = bonesBySourceName.get(sourceName);
    if (bone) bone.name = canonicalName;
  }

  const ual2RestRenamed = buildRestPose(walkSkeleton(ual2Mesh));

  // UAL2's own bind pose is a literal T-pose (the standard convention for
  // this kind of exported rig); the canonical rig's own bind pose is already
  // close to a natural standing/idle stance (retarget.ts's delta-from-bind
  // formula assumes both rigs' rest represents a comparable body
  // configuration, which does not hold here). Since every canonical clip is
  // authored as a small rotational delta from ITS OWN already-natural rest,
  // retargeting as-is leaves that delta near-identity, so
  // `sourceBind * delta` collapses to `sourceBind` -- UAL2 stays frozen in
  // its T-pose bind regardless of which clip plays. Rebasing every
  // successfully-mapped bone's retarget-purpose rest QUATERNION (position is
  // untouched -- this is an orientation problem, not a translation one, and
  // overriding position too would discard UAL2's own proportions in favour
  // of canonical's hip height/limb lengths) onto canonical's own rest
  // sidesteps this: the rotation formula then reduces to
  // `result = canonicalAnim`, i.e. UAL2 reproduces canonical's own absolute
  // orientation bone-for-bone, correct as long as the two rigs' bone-local
  // axis conventions agree for that joint (an assumption this whole built-in
  // integration already relies on -- see retarget.ts's own "known
  // limitation" on axis conventions). Verified visually: without this, UAL2
  // mannequins render in a static T-pose for every clip; with it, they play
  // the same idle/walk/etc motion canonical does, at UAL2's own proportions.
  for (const canonicalName of Object.keys(mapping)) {
    const canonicalBind = canonicalRest[canonicalName];
    const ual2Bind = ual2RestRenamed[canonicalName];
    if (canonicalBind && ual2Bind) ual2Bind.quaternion.copy(canonicalBind.quaternion);
  }

  const identityMapping: BoneMapping = Object.fromEntries(Object.keys(mapping).map((name) => [name, name]));

  const unmapped = new Set<string>();
  const animations = canonical.animations.map((clip) => {
    const { clip: retargeted, unmappedBones } = retargetClip(clip, identityMapping, canonicalRest, ual2RestRenamed);
    for (const bone of unmappedBones) unmapped.add(bone);
    return retargeted;
  });

  return { scene: ual2.scene, animations, mapping, unmappedBones: [...unmapped] };
}
